import api from './api';
import type { 
  User, 
  LoginRequest, 
  RegisterRequest, 
  AuthResponse,
  RegisterResponse,
  VerifyEmailRequest,
  ResendVerificationRequest,
  ForgotPasswordRequest,
  ResetPasswordRequest,
  DeleteAccountRequest,
  DeleteAccountResponse,
  SuccessResponse
} from '../types';

class AuthService {
  /**
   * Register a new student account
   * Note: Registration now requires email verification before login
   */
  async register(data: RegisterRequest): Promise<RegisterResponse> {
    const response = await api.post<RegisterResponse>('/auth/register', data);
    return response.data;
  }

  /**
   * Verify email with 6-digit code
   */
  async verifyEmail(data: VerifyEmailRequest): Promise<AuthResponse> {
    const response = await api.post<AuthResponse>('/auth/verify-email', data);
    
    // Store tokens after successful verification
    if (response.data.access_token) {
      localStorage.setItem('access_token', response.data.access_token);
    }
    if (response.data.refresh_token) {
      localStorage.setItem('refresh_token', response.data.refresh_token);
    }
    
    return response.data;
  }

  /**
   * Resend verification code to email
   */
  async resendVerification(data: ResendVerificationRequest): Promise<SuccessResponse> {
    const response = await api.post<SuccessResponse>('/auth/resend-verification', data);
    return response.data;
  }

  /**
   * Login and get access tokens
   */
  async login(data: LoginRequest): Promise<AuthResponse> {
    const response = await api.post<AuthResponse>('/auth/login', data);
    
    // Store tokens in localStorage
    if (response.data.access_token) {
      localStorage.setItem('access_token', response.data.access_token);
    }
    if (response.data.refresh_token) {
      localStorage.setItem('refresh_token', response.data.refresh_token);
    }
    
    return response.data;
  }

  /**
   * Logout and invalidate refresh token
   */
  async logout(): Promise<void> {
    const refreshToken = localStorage.getItem('refresh_token');
    
    try {
      if (refreshToken) {
        await api.post('/auth/logout', { refresh_token: refreshToken });
      }
    } finally {
      // Always clear local storage
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
    }
  }

  /**
   * Initiate password reset - sends code to email
   */
  async forgotPassword(data: ForgotPasswordRequest): Promise<SuccessResponse> {
    const response = await api.post<SuccessResponse>('/auth/forgot-password', data);
    return response.data;
  }

  /**
   * Reset password with code
   */
  async resetPassword(data: ResetPasswordRequest): Promise<SuccessResponse> {
    const response = await api.post<SuccessResponse>('/auth/reset-password', data);
    return response.data;
  }

  /**
   * Verify password reset code
   */
  async verifyResetCode(data: { email: string; code: string }): Promise<SuccessResponse> {
    const response = await api.post<SuccessResponse>('/auth/verify-reset-code', data);
    return response.data;
  }

  /**
   * Get current authenticated user
   */
  async getCurrentUser(): Promise<User> {
    const response = await api.get<User>('/auth/me');
    return response.data;
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return !!localStorage.getItem('access_token');
  }

  /**
   * Get access token
   */
  getAccessToken(): string | null {
    return localStorage.getItem('access_token');
  }

  /**
   * Delete user account
   * Requires password and typing "DELETE" as confirmation
   */
  async deleteAccount(data: DeleteAccountRequest): Promise<DeleteAccountResponse> {
    const response = await api.delete<DeleteAccountResponse>('/student/account/delete', {
      data
    });
    // Clear tokens after successful deletion
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    return response.data;
  }
}

export default new AuthService();
